import { computed, effect, inject, Injectable, isDevMode, NgZone, signal } from "@angular/core";
import { httpResource } from "@angular/common/http";

import { ThreeDSTokenResponseSchema } from "@modules/checkout/schemas/3ds-token-response.schema";
import { ThreeDSTokenResponse } from "@modules/checkout/types/3ds-token-response";
import { environment } from "@environments/environment";

export interface ThreeDSAuthResult {
  ExternalAuthentication: {
    Cavv: string;
    Xid?: string;
    Eci: string;
    Version: string;
    ReferenceID?: string;
  } | null;
  ExternalAuthenticationFailure: BpmpiAuthEvent | null;
}

/** Maximum time to wait for the 3DS script to load (ms). */
const SCRIPT_LOAD_TIMEOUT = 30_000;

/** Maximum time to wait for a 3DS authentication result (ms).
 *  The challenge flow requires user interaction with the issuer,
 *  so a generous timeout is needed. */
const AUTH_TIMEOUT = 5 * 60 * 1000;

@Injectable({ providedIn: "root" })
export class ThreeDSService {
  private readonly ngZone = inject(NgZone);
  private readonly _transactionId = signal<string | null>(null);

  private _lastAuthResult: ThreeDSAuthResult | null = null;
  private _lastCardFingerprint: string | null = null;

  private _scriptReady!: Promise<void>;
  private _resolveScriptReady!: () => void;

  private _onSuccess: ((event: BpmpiSuccessEvent) => void) | null = null;
  private _onFailure: ((event: BpmpiAuthEvent) => void) | null = null;
  private _onUnenrolled: ((event: BpmpiAuthEvent) => void) | null = null;
  private _onDisabled: (() => void) | null = null;
  private _onError: ((event: BpmpiAuthEvent) => void) | null = null;
  private _onUnsupportedBrand: ((event: BpmpiAuthEvent) => void) | null = null;

  /**
   * Signal set by the component when the bpmpi-form is rendered in the DOM.
   * The 3DS script reads bpmpi_accesstoken from the DOM during initialization,
   * so the hidden inputs must be present before the script loads.
   */
  private readonly _formReady = signal(false);

  /**
   * Whether the 3DS script has loaded and called onReady.
   * Used to disable the submit button while the script is still loading.
   */
  readonly scriptReady = signal(false);

  readonly token = httpResource<ThreeDSTokenResponse>(
    () => {
      const id = this._transactionId();
      if (!id) return undefined;
      return {
        url: `${environment.apiUrl}/payment/${id}/3ds-token`,
        method: "POST",
        body: {},
      };
    },
    { parse: (r) => ThreeDSTokenResponseSchema.parse(r) },
  );

  readonly isEnabled = computed(() => this.token.value()?.Enabled === true);

  constructor() {
    this._scriptReady = new Promise<void>((resolve) => {
      this._resolveScriptReady = resolve;
    });

    // Load the 3DS script when both conditions are met:
    // 1. 3DS is enabled (token returned Enabled=true)
    // 2. The bpmpi-form is rendered in the DOM (formReady=true)
    // The effect runs after change detection, and formReady() is set in
    // BpmpiFormComponent.ngAfterViewInit (also after change detection),
    // so the DOM is already updated when this effect fires.
    effect(() => {
      if (this.isEnabled() && this._formReady()) {
        this.loadScript();
      }
    });
  }

  fetchToken(transactionId: string): void {
    if (this._transactionId() !== null) return;
    this._transactionId.set(transactionId);
  }

  /**
   * Notifies the service that the bpmpi-form is rendered in the DOM.
   * Called by the component after the form view is initialized.
   */
  formReady(): void {
    this._formReady.set(true);
  }

  /**
   * Authenticates via bpmpi, caching the result per card fingerprint.
   * Subsequent calls with the same fingerprint reuse the cached result
   * so payment retries (e.g. insufficient funds) don't re-trigger the
   * authentication challenge. A changed card invalidates the cache.
   */
  async authenticate(cardFingerprint: string): Promise<ThreeDSAuthResult> {
    if (this._lastAuthResult !== null && this._lastCardFingerprint === cardFingerprint) {
      return this._lastAuthResult;
    }

    // Wait for the 3DS script to be ready (with timeout).
    try {
      await Promise.race([
        this._scriptReady,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("3DS script load timeout")), SCRIPT_LOAD_TIMEOUT),
        ),
      ]);
    } catch {
      const fallback: ThreeDSAuthResult = {
        ExternalAuthentication: null,
        ExternalAuthenticationFailure: null,
      };
      this._cacheResult(cardFingerprint, fallback);
      return fallback;
    }

    if (typeof window.bpmpi_authenticate !== "function") {
      const fallback: ThreeDSAuthResult = {
        ExternalAuthentication: null,
        ExternalAuthenticationFailure: null,
      };
      this._cacheResult(cardFingerprint, fallback);
      return fallback;
    }

    return new Promise((resolve) => {
      let settled = false;

      const settle = (result: ThreeDSAuthResult) => {
        if (settled) return;
        settled = true;
        clearTimeout(authTimeoutId);
        this._cacheResult(cardFingerprint, result);
        resolve(result);
      };

      const authTimeoutId = setTimeout(() => {
        this.ngZone.run(() => {
          settle({
            ExternalAuthentication: null,
            ExternalAuthenticationFailure: null,
          });
        });
      }, AUTH_TIMEOUT);

      // Update dynamic callback references — bpmpi_config is defined once
      // in loadScript() and the BP.Mpi script caches the result at init.
      // Re-assigning window.bpmpi_config does NOT update cached callbacks.
      this._onSuccess = (event) =>
        this.ngZone.run(() =>
          settle({
            ExternalAuthentication: {
              Cavv: event.Cavv,
              Xid: event.Xid,
              Eci: event.Eci,
              Version: event.Version,
              ReferenceID: event.ReferenceId,
            },
            ExternalAuthenticationFailure: null,
          }),
        );

      this._onFailure = (event) =>
        this.ngZone.run(() =>
          settle({ ExternalAuthentication: null, ExternalAuthenticationFailure: event }),
        );

      this._onUnenrolled = (event) =>
        this.ngZone.run(() =>
          settle({ ExternalAuthentication: null, ExternalAuthenticationFailure: event }),
        );

      this._onDisabled = () =>
        this.ngZone.run(() =>
          settle({ ExternalAuthentication: null, ExternalAuthenticationFailure: null }),
        );

      this._onError = (event) =>
        this.ngZone.run(() =>
          settle({ ExternalAuthentication: null, ExternalAuthenticationFailure: event }),
        );

      this._onUnsupportedBrand = (event) =>
        this.ngZone.run(() =>
          settle({ ExternalAuthentication: null, ExternalAuthenticationFailure: event }),
        );

      window.bpmpi_authenticate();
    });
  }

  private _cacheResult(fingerprint: string, result: ThreeDSAuthResult): void {
    this._lastCardFingerprint = fingerprint;
    this._lastAuthResult = result;
  }

  private loadScript(): void {
    if (document.getElementById("bpmpi-script")) return;

    // Define bpmpi_config ONCE with all callbacks. The BP.Mpi script caches
    // the result of bpmpi_config() at initialization (var T = bpmpi_config())
    // and never re-reads it. Each callback delegates to a dynamic reference
    // that is updated before each authenticate() call.
    window.bpmpi_config = () => ({
      onReady: () => {
        this.ngZone.run(() => {
          this.scriptReady.set(true);
          this._resolveScriptReady();
        });
      },
      onSuccess: (event: BpmpiSuccessEvent) => {
        this._onSuccess?.(event);
      },
      onFailure: (event: BpmpiAuthEvent) => {
        this._onFailure?.(event);
      },
      onUnenrolled: (event: BpmpiAuthEvent) => {
        this._onUnenrolled?.(event);
      },
      onDisabled: () => {
        this._onDisabled?.();
      },
      onError: (event: BpmpiAuthEvent) => {
        this._onError?.(event);
      },
      onUnsupportedBrand: (event: BpmpiAuthEvent) => {
        this._onUnsupportedBrand?.(event);
      },
      Environment: environment.threeDSEnvironment,
      Debug: isDevMode(),
    });

    const script = document.createElement("script");
    script.id = "bpmpi-script";
    script.src = "/assets/scripts/BP.Mpi.3ds20.min.js";
    document.body.appendChild(script);
  }
}
